import { useState } from 'react'

import isEqual from 'lodash/isEqual'
import moment from 'moment'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { CostCenterOption } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/hooks/useCostCenterByLegalEntities'
import {
  DEFAULT_ASSIGNMENT_COST_CENTER_ID,
  DEFAULT_ASSIGNMENT_PERCENTAGE,
  defaultAssignment,
  defaultStructuredAssignment,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/defaults'
import { formatAssignments } from 'modules/cost_centers/components/CostCenterAssigments/lib/formatter'
import {
  CostCenterAssignment,
  FormattedAssignment,
  FormattedAssignmentWithDeletion,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

import { useAssignmentsValidator } from './useAssignmentsValidator'
import { DateRange } from './validations/overlaps'

// ValueAssignment type used by the legacy CostCenterPicker (DimensionMultipleValuePicker)
interface ValueAssignment {
  name: string
  id: string
  percentage: number
}

interface Props {
  currentAssignments: (FormattedAssignmentWithDeletion | FormattedAssignment)[]
  allAssignments: CostCenterAssignment[]
  defaultDateRange?: { startDate: string; endDate: string }
  hasNewStructureEnabled?: boolean
  isCreating?: boolean
}

/**
 * Get current (active) assignments - those without an end date
 */
const getCurrentAssignments = (
  allAssignments: CostCenterAssignment[],
  hasNewStructureEnabled: boolean
): FormattedAssignment[] => {
  const currentAssignments = allAssignments.filter((assignment) => !assignment.endDate)
  return formatAssignments(currentAssignments, hasNewStructureEnabled)
}

/**
 * Calculate end date for current assignments (one day before the new start date)
 */
const calculateEndDateForCurrentAssignments = (newStartDate: string): string => {
  return moment(newStartDate).subtract(1, 'day').format('YYYY-MM-DD')
}

export const useAssignmentsManager = ({
  currentAssignments,
  allAssignments,
  defaultDateRange,
  hasNewStructureEnabled = false,
  isCreating = false,
}: Props) => {
  // For new structure enabled in creation mode, prepopulate with current (active) assignments
  const shouldPrepopulateWithCurrentAssignments = hasNewStructureEnabled && isCreating
  const activeAssignments = shouldPrepopulateWithCurrentAssignments
    ? getCurrentAssignments(allAssignments, hasNewStructureEnabled)
    : []

  // Mark prepopulated assignments as default so their cost center selectors are disabled
  const prepopulatedAssignments: FormattedAssignment[] = activeAssignments.map((assignment) => ({
    ...assignment,
    isDefault: true,
  }))

  //------------ State Management ------------

  const [assignmentToCreateTemplate, setAssignmentToCreateTemplate] = useState<
    FormattedAssignment[]
  >([])
  const [userInteracted, setUserInteracted] = useState(false)
  const [assignmentsToCreate, setAssignmentsToCreate] = useState<FormattedAssignment[]>(
    shouldPrepopulateWithCurrentAssignments ? prepopulatedAssignments : []
  )
  const [assignmentsToUpdate, setAssignmentsToUpdate] =
    useState<FormattedAssignmentWithDeletion[]>(currentAssignments)
  const [assignments, setAssignments] = useState<FormattedAssignment[]>(
    shouldPrepopulateWithCurrentAssignments
      ? [...prepopulatedAssignments]
      : [...assignmentsToCreate, ...assignmentsToUpdate]
  )

  // Track the current assignments without end date for updating when start date changes
  const [currentAssignmentsToEnd, setCurrentAssignmentsToEnd] = useState<
    FormattedAssignmentWithDeletion[]
  >([])

  //------------ Date Range State ------------

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: currentAssignments[0]?.startDate || defaultDateRange?.startDate || '',
    endDate: currentAssignments[0]?.endDate || defaultDateRange?.endDate || '',
  })

  //------------ Initialize Current Assignments for New Structure ------------

  useUnsafeEffect(() => {
    if (shouldPrepopulateWithCurrentAssignments && currentAssignmentsToEnd.length === 0) {
      const activeRawAssignments = allAssignments.filter((a) => !a.endDate)
      if (activeRawAssignments.length > 0) {
        const formattedForUpdate = formatAssignments(
          activeRawAssignments,
          hasNewStructureEnabled
        ).map((a) => ({
          ...a,
          markedForDeletion: false,
        }))
        setCurrentAssignmentsToEnd(formattedForUpdate)
      }
    }
  }, [
    shouldPrepopulateWithCurrentAssignments,
    allAssignments,
    hasNewStructureEnabled,
    currentAssignmentsToEnd.length,
  ])

  //------------ Track User Interaction ------------

  useUnsafeEffect(() => {
    if (userInteracted) return

    const assignmentsAreDifferent = !isEqual(assignments, [
      ...assignmentsToCreate,
      ...assignmentsToUpdate,
    ])

    if (assignmentsAreDifferent) {
      setUserInteracted(true)
    }
  }, [assignments, userInteracted, assignmentsToCreate, assignmentsToUpdate])

  //------------ Validations ------------

  const { validForSave, validations } = useAssignmentsValidator({
    selectedDateRange,
    currentAssignments: currentAssignments,
    displayedAssignments: assignments,
    allAssignments,
    userInteracted: userInteracted,
    hasNewStructureEnabled,
    isCreating,
  })

  //------------ Date Range Management ------------

  const setNewDateRange = ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
    const dateToAssign = {
      ...(startDate ? { startDate } : {}),
      ...(typeof endDate === 'string' ? { endDate } : {}),
    }

    setSelectedDateRange((prevDateRange) => ({
      ...prevDateRange,
      ...dateToAssign,
    }))

    setAssignmentToCreateTemplate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })

    setAssignmentsToCreate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })

    // When in creation mode with new structure enabled, update the current assignments
    // to have end date set to one day before the new start date
    if (
      shouldPrepopulateWithCurrentAssignments &&
      startDate &&
      currentAssignmentsToEnd.length > 0
    ) {
      const endDateForCurrentAssignments = calculateEndDateForCurrentAssignments(startDate)
      const updatedCurrentAssignments = currentAssignmentsToEnd.map((assignment) => ({
        ...assignment,
        endDate: endDateForCurrentAssignments,
      }))
      setAssignmentsToUpdate(updatedCurrentAssignments)
    } else {
      setAssignmentsToUpdate((prevAssignments) => {
        return prevAssignments.map((assignment) => ({
          ...assignment,
          ...dateToAssign,
        }))
      })
    }
  }

  //==========================================================================
  // Legacy Mode Functions
  //==========================================================================

  const assignmentsToUpdateNotMarkedForDeletion = assignmentsToUpdate.filter(
    (v) => !v.markedForDeletion
  )

  //------------ Legacy: Add New Assignment ------------

  const addNewAssignment = useUnsafeCallback(() => {
    const newAssignment = defaultAssignment({
      startDate: String(selectedDateRange.startDate),
      endDate: selectedDateRange.endDate,
      previousCostCenterId: Math.min(...assignments.map((item) => Number(item.costCenterId))),
    })

    setAssignmentToCreateTemplate((prevAssignments) => [...prevAssignments, newAssignment])
    setAssignments([...assignments, newAssignment])

    track(trackingEvents.accounting.addNewCostCenterToAssignment, {
      countAdded:
        assignments.filter((item) => item.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE).length + 1,
    })
  }, [assignments, selectedDateRange, setAssignmentToCreateTemplate, setAssignments])

  //------------ Legacy: Delete Assignment ------------

  const deleteValue = (value: ValueAssignment) => {
    const storedAssignment = assignmentsToUpdate.find((v) => String(v.costCenterId) === value.id)
    const assignmentInCreate = assignmentsToCreate.find((v) => String(v.costCenterId) === value.id)

    if (storedAssignment) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== storedAssignment.id),
        { ...storedAssignment, markedForDeletion: true },
      ])
    }

    if (assignmentInCreate) {
      setAssignmentsToCreate([
        ...assignmentsToCreate.filter((v) => v.costCenterId !== Number(value.id)),
      ])
    }

    setAssignments([...assignments.filter((v) => String(v.costCenterId) !== value.id)])
  }

  //------------ Legacy: Template Management Helper ------------

  const manageCostcenterInAssignmentToCreateInTemplate = (
    value: FormattedAssignment,
    newValue: ValueAssignment
  ) => {
    const { costCenterId } = value

    if (
      newValue.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE ||
      Number(newValue.id) <= DEFAULT_ASSIGNMENT_COST_CENTER_ID
    ) {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.costCenterId !== costCenterId),
        { ...value, ...newValue, costCenterId: Number(newValue.id) },
      ])
    } else {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.costCenterId !== costCenterId),
      ])

      setAssignmentsToCreate([
        ...assignmentsToCreate,
        { ...value, ...newValue, costCenterId: Number(newValue.id) },
      ])
    }
  }

  //------------ Legacy: Update Assignment ------------

  const updateAssignment = (valueId: string, newValue: ValueAssignment) => {
    const assignment = assignments.find((v) => v.costCenterId == Number(valueId))

    if (!assignment) return

    const costCenterInUpdateAssignments = assignmentsToUpdateNotMarkedForDeletion.find(
      (v) => v.costCenterId == Number(valueId)
    )

    const costCenterInAssignmentToCreateInTemplate = assignmentToCreateTemplate.find(
      (v) => v.costCenterId == Number(valueId)
    )

    if (costCenterInUpdateAssignments) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== costCenterInUpdateAssignments.id),
        {
          ...costCenterInUpdateAssignments,
          percentage: newValue.percentage,
          costCenterId: Number(newValue.id),
        },
      ])
    } else if (costCenterInAssignmentToCreateInTemplate) {
      manageCostcenterInAssignmentToCreateInTemplate(
        costCenterInAssignmentToCreateInTemplate,
        newValue
      )
    } else {
      const assignmentToPatch = assignmentsToCreate.find((v) => v.costCenterId == Number(valueId))

      if (!assignmentToPatch) return

      setAssignmentsToCreate([
        ...assignmentsToCreate.filter((v) => v.costCenterId !== Number(newValue.id)),
        { ...assignmentToPatch, ...newValue, costCenterId: Number(newValue.id) },
      ])
    }

    // In order to not to update the order of the assignments, we need to map the assignments and
    // update the assignment with the new value
    setAssignments(
      assignments.map((item) =>
        String(item.costCenterId) === valueId
          ? {
              ...item,
              ...newValue,
              costCenterId: Number(newValue.id),
            }
          : item
      )
    )
  }

  //==========================================================================
  // Structured Mode Functions
  //==========================================================================

  //------------ Structured: Add New Assignment ------------

  const addNewStructuredAssignment = useUnsafeCallback(() => {
    const newAssignment = defaultStructuredAssignment({
      startDate: String(selectedDateRange.startDate),
      endDate: selectedDateRange.endDate,
    })

    setAssignmentToCreateTemplate((prevAssignments) => [...prevAssignments, newAssignment])
    setAssignments([...assignments, newAssignment])

    track(trackingEvents.accounting.addNewCostCenterToAssignment, {
      countAdded:
        assignments.filter((item) => item.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE).length + 1,
    })
  }, [assignments, selectedDateRange, setAssignmentToCreateTemplate, setAssignments])

  //------------ Structured: Delete Assignment ------------

  const deleteStructuredAssignment = (assignmentId: string) => {
    const storedAssignment = assignmentsToUpdate.find((v) => v.id === assignmentId)
    const assignmentInCreate = assignmentsToCreate.find((v) => v.id === assignmentId)
    const assignmentInTemplate = assignmentToCreateTemplate.find((v) => v.id === assignmentId)

    if (storedAssignment) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== storedAssignment.id),
        { ...storedAssignment, markedForDeletion: true },
      ])
    }

    if (assignmentInCreate) {
      setAssignmentsToCreate([...assignmentsToCreate.filter((v) => v.id !== assignmentId)])
    }

    if (assignmentInTemplate) {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.id !== assignmentId),
      ])
    }

    setAssignments([...assignments.filter((v) => v.id !== assignmentId)])
  }

  //------------ Structured: Generic Field Updater ------------

  const updateStructuredField = (assignmentId: string, updates: Partial<FormattedAssignment>) => {
    const currentAssignment = assignments.find((v) => v.id === assignmentId)
    if (!currentAssignment) return

    const updatedAssignment = { ...currentAssignment, ...updates }

    // Check if this is in the update list (existing assignment)
    const existingInUpdate = assignmentsToUpdate.find((v) => v.id === assignmentId)
    if (existingInUpdate) {
      setAssignmentsToUpdate(
        assignmentsToUpdate.map((v) => (v.id === assignmentId ? { ...v, ...updates } : v))
      )
    }

    // Check if this is in the template (new assignment not yet valid)
    const existingInTemplate = assignmentToCreateTemplate.find((v) => v.id === assignmentId)
    if (existingInTemplate) {
      const updatedTemplateAssignment = { ...existingInTemplate, ...updates }

      // Check if assignment is now valid (has a cost center and valid percentage)
      const hasValidCostCenter = updatedTemplateAssignment.costCenterId != null
      const hasValidPercentage =
        updatedTemplateAssignment.percentage !== DEFAULT_ASSIGNMENT_PERCENTAGE &&
        updatedTemplateAssignment.percentage > 0

      if (hasValidCostCenter && hasValidPercentage) {
        // Move from template to create list
        setAssignmentToCreateTemplate(
          assignmentToCreateTemplate.filter((v) => v.id !== assignmentId)
        )
        setAssignmentsToCreate([...assignmentsToCreate, updatedTemplateAssignment])
      } else {
        // Keep in template
        setAssignmentToCreateTemplate(
          assignmentToCreateTemplate.map((v) =>
            v.id === assignmentId ? updatedTemplateAssignment : v
          )
        )
      }
    }

    // Check if this is in the create list
    const existingInCreate = assignmentsToCreate.find((v) => v.id === assignmentId)
    if (existingInCreate) {
      setAssignmentsToCreate(
        assignmentsToCreate.map((v) => (v.id === assignmentId ? { ...v, ...updates } : v))
      )
    }

    // Update the display list
    setAssignments(assignments.map((item) => (item.id === assignmentId ? updatedAssignment : item)))
  }

  //------------ Structured: Update Cost Center ------------

  const updateCostCenter = (assignmentId: string, option: CostCenterOption | null) => {
    // If selecting a new cost center, remove it from any other assignments (deduplication)
    if (option) {
      const otherAssignments = assignments.filter((a) => a.id !== assignmentId)
      otherAssignments.forEach((otherAssignment) => {
        if (otherAssignment.costCenterId === Number(option.id)) {
          // Remove the cost center from the other assignment
          updateStructuredField(otherAssignment.id, {
            costCenterId: undefined,
            name: '',
          })
        }
      })
    }

    updateStructuredField(assignmentId, {
      costCenterId: option ? Number(option.id) : undefined,
      name: option?.name ?? '',
    })
  }

  //------------ Structured: Update Percentage ------------

  const updateStructuredPercentage = (assignmentId: string, percentage: number) => {
    updateStructuredField(assignmentId, { percentage })
  }

  //==========================================================================
  // Return
  //==========================================================================

  // When creating with new structure enabled and there's only a single prepopulated default
  // assignment, we don't support custom assignments yet — the user should be able to view
  // but not create or modify.
  const hasSingleDefaultAssignment =
    shouldPrepopulateWithCurrentAssignments && prepopulatedAssignments.length === 1

  return {
    assignmentsToCreate,
    assignmentsToUpdate,
    selectedDateRange,
    assignments,
    validForSave,
    validations,
    hasSingleDefaultAssignment,
    // Legacy mode functions
    addNewAssignment,
    deleteValue,
    updateAssignment,
    // Structured mode functions
    addNewStructuredAssignment,
    deleteStructuredAssignment,
    updateCostCenter,
    updateStructuredPercentage,
    // Shared functions
    setAssignmentsToCreate,
    setAssignmentsToUpdate,
    setNewDateRange,
  }
}
